import * as THREE from 'three';

/**
 * @typedef {Object} CircleCollider
 * @property {'circle'} type
 * @property {number} radius
 */

/**
 * @typedef {Object} BoxCollider
 * @property {'box'} type
 * @property {number} width
 * @property {number} depth
 * @property {number} rotation - Rotation in radians
 */

/**
 * @typedef {CircleCollider | BoxCollider} Collider
 */

/**
 * Handles collision detection and resolution for all entities.
 * Supports circle-circle and box-circle collision detection.
 */
export class CollisionSystem {
    /** @type {Map<any, Collider>} */
    #colliders = new Map();

    /**
     * Register an entity with a circle collider
     * @param {any} entity - The entity to register
     * @param {number} radius - The collision radius
     */
    registerCircle(entity, radius) {
        this.#colliders.set(entity, {
            type: 'circle',
            radius
        });
    }

    /**
     * Register an entity with a box collider
     * @param {any} entity - The entity to register
     * @param {number} width - The box width
     * @param {number} depth - The box depth
     * @param {number} rotation - The box rotation in radians
     */
    registerBox(entity, width, depth, rotation = 0) {
        this.#colliders.set(entity, {
            type: 'box',
            width,
            depth,
            rotation
        });
    }

    /**
     * Unregister an entity from collision detection
     * @param {any} entity - The entity to unregister
     */
    unregister(entity) {
        this.#colliders.delete(entity);
    }

    /**
     * Test and resolve collisions for a proposed new position
     * @param {any} entity - The entity being moved
     * @param {THREE.Vector3} proposedPosition - The proposed new position
     * @param {THREE.Vector3} currentPosition - The current position
     * @returns {THREE.Vector3} Adjusted position after collision resolution
     */
    resolveMovement(entity, proposedPosition, currentPosition) {
        if (entity && entity.collidable === false) {
            return proposedPosition.clone();
        }

        const entityCollider = this.#colliders.get(entity);
        if (!entityCollider || entityCollider.type !== 'circle') {
            return proposedPosition.clone();
        }

        const adjustedPosition = proposedPosition.clone();
        const entityRadius = entityCollider.radius;

        // Check collisions with all other registered entities
        for (const [other, otherCollider] of this.#colliders.entries()) {
            if (other === entity) continue;
            if (other && other.collidable === false) continue;

            if (otherCollider.type === 'circle') {
                this.#resolveCircleCircle(
                    adjustedPosition,
                    entityRadius,
                    other.position,
                    otherCollider.radius
                );
            } else if (otherCollider.type === 'box') {
                this.#resolveCircleBox(
                    adjustedPosition,
                    entityRadius,
                    other.position,
                    otherCollider.width,
                    otherCollider.depth,
                    otherCollider.rotation
                );
            }
        }

        return adjustedPosition;
    }

    /**
     * Resolve circle-circle collision
     * @param {THREE.Vector3} circlePos - Position of the moving circle
     * @param {number} circleRadius - Radius of the moving circle
     * @param {THREE.Vector3} otherPos - Position of the other circle
     * @param {number} otherRadius - Radius of the other circle
     */
    #resolveCircleCircle(circlePos, circleRadius, otherPos, otherRadius) {
        const dx = circlePos.x - otherPos.x;
        const dz = circlePos.z - otherPos.z;
        const distSq = dx * dx + dz * dz;
        const minDist = circleRadius + otherRadius;

        if (distSq < minDist * minDist && distSq > 0) {
            // Collision detected, calculate push-back vector
            const dist = Math.sqrt(distSq);
            const overlap = minDist - dist;

            // Normalize and scale by overlap
            const pushX = (dx / dist) * overlap;
            const pushZ = (dz / dist) * overlap;

            // Apply pushback
            circlePos.x += pushX;
            circlePos.z += pushZ;
        }
    }

    /**
     * Resolve circle-box collision
     * @param {THREE.Vector3} circlePos - Position of the moving circle
     * @param {number} circleRadius - Radius of the moving circle
     * @param {THREE.Vector3} boxPos - Position of the box
     * @param {number} boxWidth - Width of the box
     * @param {number} boxDepth - Depth of the box
     * @param {number} boxRotation - Rotation of the box in radians
     */
    #resolveCircleBox(circlePos, circleRadius, boxPos, boxWidth, boxDepth, boxRotation) {
        const halfWidth = boxWidth / 2;
        const halfDepth = boxDepth / 2;

        // Transform circle position to box's local coordinates
        const dx = circlePos.x - boxPos.x;
        const dz = circlePos.z - boxPos.z;

        // Rotate the vector to align with box orientation
        const cosRot = Math.cos(-boxRotation);
        const sinRot = Math.sin(-boxRotation);

        const rotatedDx = dx * cosRot - dz * sinRot;
        const rotatedDz = dx * sinRot + dz * cosRot;

        // Find closest point on box to circle
        const closestX = Math.max(-halfWidth, Math.min(halfWidth, rotatedDx));
        const closestZ = Math.max(-halfDepth, Math.min(halfDepth, rotatedDz));

        // Check if circle is inside box
        const insideX = Math.abs(rotatedDx) < halfWidth;
        const insideZ = Math.abs(rotatedDz) < halfDepth;

        if (insideX && insideZ) {
            // Circle is inside box, find the smallest axis to push out along
            const xOverlap = halfWidth - Math.abs(rotatedDx);
            const zOverlap = halfDepth - Math.abs(rotatedDz);

            // Push along the axis with the smallest overlap
            if (xOverlap < zOverlap) {
                // Push along x axis
                const pushDirX = rotatedDx > 0 ? 1 : -1;

                // Rotate back to world space
                const worldPushX = pushDirX * xOverlap * cosRot;
                const worldPushZ = pushDirX * xOverlap * sinRot;

                circlePos.x += worldPushX;
                circlePos.z += worldPushZ;
            } else {
                // Push along z axis
                const pushDirZ = rotatedDz > 0 ? 1 : -1;

                // Rotate back to world space
                const worldPushX = -pushDirZ * zOverlap * sinRot;
                const worldPushZ = pushDirZ * zOverlap * cosRot;

                circlePos.x += worldPushX;
                circlePos.z += worldPushZ;
            }
        } else {
            // Check distance to closest point
            const distToBorderX = rotatedDx - closestX;
            const distToBorderZ = rotatedDz - closestZ;
            const distSq = distToBorderX * distToBorderX + distToBorderZ * distToBorderZ;

            if (distSq < circleRadius * circleRadius && distSq > 0) {
                // Collision with edge, calculate push-back
                const dist = Math.sqrt(distSq);
                const overlap = circleRadius - dist;

                // Normalize and scale by overlap
                const pushDirX = distToBorderX / dist;
                const pushDirZ = distToBorderZ / dist;

                // Rotate back to world space
                const worldPushX = overlap * (pushDirX * cosRot + pushDirZ * sinRot);
                const worldPushZ = overlap * (-pushDirX * sinRot + pushDirZ * cosRot);

                circlePos.x += worldPushX;
                circlePos.z += worldPushZ;
            }
        }
    }

    /**
     * Check if there's a collision between two entities
     * @param {any} entity1 - First entity
     * @param {any} entity2 - Second entity
     * @returns {boolean} True if collision detected
     */
    checkCollision(entity1, entity2) {
        if ((entity1 && entity1.collidable === false) || (entity2 && entity2.collidable === false)) {
            return false;
        }

        const collider1 = this.#colliders.get(entity1);
        const collider2 = this.#colliders.get(entity2);

        if (!collider1 || !collider2) return false;

        if (collider1.type === 'circle' && collider2.type === 'circle') {
            const dx = entity1.position.x - entity2.position.x;
            const dz = entity1.position.z - entity2.position.z;
            const distSq = dx * dx + dz * dz;
            const minDist = collider1.radius + collider2.radius;
            return distSq < minDist * minDist;
        }

        // TODO: Implement other collision type checks if needed
        return false;
    }

    /**
     * Get all registered entities
     * @returns {any[]}
     */
    getAllEntities() {
        return Array.from(this.#colliders.keys());
    }

    /**
     * Clear all registered entities
     */
    clear() {
        this.#colliders.clear();
    }
}
